import { useEffect, useMemo } from 'react';
import { Box, HStack, Text, useColorMode } from '@chakra-ui/react';
import { ToolbarProps } from 'react-big-calendar';

import {
	useCalendarSettings,
	useAutoContrastColor,
	useShortcodeSettings,
	anyFilterEnabled,
	styles,
} from '../../../lib';
import { FiltersDrawer } from './FiltersDrawer';
import { formatNavigation, ViewMenu } from '.';
import { ChevronLeft, ChevronRight } from '../../icons';
import { useToolbarLogic } from './useToolbarLogic';
import { DateInputField } from './DateInputField';

export const DesktopToolbar = ({
	date,
	view,
	views,
	onNavigate,
	onView,
	setCurrentMonth,
}: ToolbarProps & { setCurrentMonth: React.Dispatch<React.SetStateAction<string>> }) => {
	const { colorMode } = useColorMode();
	const {
		inputValue,
		inputType,
		handlePrevClick,
		handleNextClick,
		handleTodayClick,
		handleDateInputChange,
		createViewChangeHandler,
		viewLabels,
	} = useToolbarLogic(date, view, onNavigate, onView);

	const { color: contrastColor, ref } = useAutoContrastColor();
	const { settings } = useCalendarSettings();
	const { settings: shortcodeSettings } = useShortcodeSettings();

	useEffect(() => {
		setCurrentMonth(inputValue);
	}, [inputValue, setCurrentMonth]);

	const { todayText } = formatNavigation(date, view);
	const { iconStyle } = styles.toolbar;
	const { enableSearchFilter, enableLocationFilter, enableCategoryFilter, enableVenueFilter, enableTagFilter } =
		settings;

	// Checks for rendering filters through settings/shortcodes
	const shortcodeFiltersAllowed = shortcodeSettings.filters !== false;
	const filtersExplicitlyEnabled = shortcodeSettings.filters === true;
	const anyFiltersEnabled = useMemo(
		() =>
			anyFilterEnabled(
				enableSearchFilter,
				enableLocationFilter,
				enableCategoryFilter,
				enableVenueFilter,
				enableTagFilter
			),
		[enableSearchFilter, enableLocationFilter, enableCategoryFilter, enableVenueFilter, enableTagFilter]
	);
	return (
		<HStack
			borderRadius={settings.radius.toolbar}
			spacing={4}
			p={4}
			gap={2}
			bg={settings.styles[colorMode]?.calendarToolbarBackgroundColor || 'white'}
			justify='space-between'
			align='center'
			className='calendar-toolbar'
		>
			<HStack ref={ref} w='full' alignItems='center' justifyContent='flex-start' px={1} py={2}>
				<Box onClick={handlePrevClick} cursor='pointer'>
					<ChevronLeft size={18} fill={contrastColor} style={iconStyle} />
				</Box>
				<Box onClick={handleNextClick} cursor='pointer'>
					<ChevronRight size={18} fill={contrastColor} style={iconStyle} />
				</Box>
				<Text onClick={handleTodayClick} cursor='pointer' paddingInline={4} color={contrastColor}>
					{todayText}
				</Text>
				<DateInputField
					inputType={inputType}
					inputValue={inputValue}
					onChange={handleDateInputChange}
					isDesktop={true}
				/>
			</HStack>
			{shortcodeFiltersAllowed && (anyFiltersEnabled || filtersExplicitlyEnabled) && (
				<HStack>
					<FiltersDrawer border={false} />
				</HStack>
			)}
			<ViewMenu
				isDesktop={true}
				views={views}
				createViewChangeHandler={createViewChangeHandler}
				viewLabels={viewLabels}
			/>
		</HStack>
	);
};
